// src/pages/Users/Messaging/SettingsMessages.jsx
import React, { useEffect, useState } from "react";
import {
  getTotalMessageCount,
  deleteMessagesByDateRange,
} from "../../../services/messageServices";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import { FaEnvelope } from "react-icons/fa";

const SettingsMessages = () => {
  const [messageCount, setMessageCount] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const count = await getTotalMessageCount();
        setMessageCount(Number(count) || 0);
      } catch (error) {
        console.error(error);
        toast.error(
          "Erreur lors de la récupération du nombre total de messages."
        );
      } finally {
        setLoading(false);
      }
    };
    fetchCount();
  }, []);

  const handlePurge = async (e) => {
    e.preventDefault();

    if (!startDate || !endDate) {
      toast.error("Veuillez sélectionner une date de début et une date de fin.");
      return;
    }

    const dStart = new Date(startDate);
    const dEnd = new Date(endDate);

    if (dStart > dEnd) {
      toast.error("La date de début ne peut pas être postérieure à la date de fin.");
      return;
    }

    const ok = window.confirm(
      `Confirmez-vous la suppression des messages entre le ${startDate} et le ${endDate} ?`
    );
    if (!ok) return;

    try {
      setIsSubmitting(true);
      const result = await deleteMessagesByDateRange(startDate, endDate);
      toast.success(result?.message || "Messages supprimés.");
      const newCount = await getTotalMessageCount();
      setMessageCount(Number(newCount) || 0);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la suppression des messages.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const prettyRange =
    startDate && endDate
      ? new Intl.DateTimeFormat("fr-FR").formatRange
        ? new Intl.DateTimeFormat("fr-FR").formatRange(
            new Date(startDate),
            new Date(endDate)
          )
        : `${new Date(startDate).toLocaleDateString("fr-FR")} → ${new Date(
            endDate
          ).toLocaleDateString("fr-FR")}`
      : "—";

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-1 sm:px-1">
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
          {/* HEADER */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8">
            <h2 className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
              Suppression des Messages anciens
            </h2>
            <p className="text-white/90 text-sm sm:text-[15px] mt-1">
              Purgez les messages anciens entre utilisateurs/enseignants/administrateurs pour alléger la base
              et garder l’application rapide.
            </p>
          </div>

          {/* CONTENT */}
          <div className="p-4 sm:p-6 space-y-6">
            {/* Stat & note sécurité */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Total messages
                  </p>
                  <p className="mt-1 text-2xl font-bold text-slate-800 dark:text-slate-100">
                    {loading ? "…" : messageCount}
                  </p>
                </div>

                <div
                  className="h-12 w-12 rounded-xl flex items-center justify-center
               bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500
               text-white shadow"
                  aria-hidden="true"
                  title="Messages"
                >
                  <FaEnvelope className="text-xl" />
                </div>
              </div>

              <div className="lg:col-span-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4">
                <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                  Les messages restent stockés sans limite de temps. Pour éviter
                  l’encombrement, supprimez régulièrement les anciennes
                  conversations (ex. saisons passées). Cette action est
                  <span className="font-semibold"> définitive</span>.
                </p>
              </div>
            </div>

            {/* Formulaire */}
            <form onSubmit={handlePurge} className="space-y-5">
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Date de début
                    </label>
                    <input
                      type="date"
                      className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700
                                 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100
                                 focus:outline-none focus:ring-2 focus:ring-violet-400"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      max={endDate || undefined}
                      required
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Date de fin
                    </label>
                    <input
                      type="date"
                      className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700
                                 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100
                                 focus:outline-none focus:ring-2 focus:ring-violet-400"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate || undefined}
                      required
                    />
                  </div>
                </div>

                <div className="mt-3 rounded-lg bg-slate-50/70 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 px-3 py-2">
                  <p className="text-xs text-slate-600 dark:text-slate-400">
                    Période sélectionnée :{" "}
                    <span className="font-medium">{prettyRange}</span>
                  </p>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  text={
                    isSubmitting
                      ? "Suppression…"
                      : "Supprimer les messages sur cette période"
                  }
                  type="submit"
                  className="!rounded-xl btn-error gap-2"
                  disabled={isSubmitting}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Styles locaux */}
      <style>{`
        input::placeholder { color: #94a3b8; }
        .dark input::placeholder { color: #94a3b8; opacity: .7; }
        html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
      `}</style>
    </section>
  );
};

export default SettingsMessages;
